import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/authService';
import { AddTeamMemberPayload, ApiService, TeamClientAssignmentDto, TeamMemberDto, TeamRole as ApiTeamRole, UpdateTeamMemberPayload } from '../services/api';
import { useAuth } from './AuthContext';

export type TeamRole = 'OWNER' | 'ADMIN' | 'LEAD' | 'MEMBER' | 'VIEWER';
export type EngagementRole = 'CONTRIBUTOR' | 'VIEWER';

export interface Team {
  id: string;
  name: string;
  description?: string | null;
  isProtected?: boolean;
  membersCount?: number;
  clientsCount?: number;
  createdAt: string;
}

export interface TeamMember {
  id: string;
  email: string;
  teamId: string;
  role: TeamRole;
  status?: string;
  firstName?: string | null;
  lastName?: string | null;
  invitedAt?: string | null;
  joinedAt?: string | null;
}

export interface ClientTeamAssignment {
  id: string;
  clientId: string;
  teamId: string;
  role: EngagementRole;
  assignedBy: string; // userId
  assignedAt: string;
}

export interface ClientMemberAssignment {
  id: string;
  clientId: string;
  userId: string;
  role: EngagementRole;
  assignedBy: string; // userId
  assignedAt: string;
}

export interface TeamClientAssignment {
  id: string;
  teamId: string;
  role: 'LEAD' | 'CONTRIBUTOR';
  createdAt: string;
  client: {
    id: string;
    companyName: string | null;
    email: string | null;
    tin: string | null;
    cacNumber: string | null;
  };
}

interface TeamState {
  teams: Team[];
  teamMembers: TeamMember[];
  // Legacy, used by Clients page (currently sample data)
  clientTeamAssignments: ClientTeamAssignment[];
  // Real team->client assignments from backend
  teamClientAssignments: TeamClientAssignment[];
  clientMemberAssignments: ClientMemberAssignment[];
}

interface TeamContextValue extends TeamState {
  assignTeamToClient: (clientId: string, teamId: string, role: EngagementRole) => Promise<void>;
  unassignTeamFromClient: (clientId: string, teamId: string) => void;
  // Team-level members management
  addMemberToTeam: (teamId: string, payload: AddTeamMemberPayload) => Promise<void>;
  updateTeamMember: (teamId: string, memberId: string, payload: UpdateTeamMemberPayload) => Promise<void>;
  removeMemberFromTeam: (teamId: string, memberId: string) => Promise<void>;
  loadTeamMembers: (teamId: string) => Promise<void>;
  loadTeamClients: (teamId: string) => Promise<void>;
  // Legacy client-level assignments kept for compatibility (not used in UI)
  addMemberToClient: (clientId: string, userId: string, role: EngagementRole) => void;
  removeMemberFromClient: (clientId: string, userId: string) => void;
  createTeam: (input: Pick<Team, 'name' | 'description'>) => Promise<Team>;
  deleteTeam: (teamId: string) => Promise<void>;
  refreshTeams: () => Promise<void>;
  isLoadingTeams: boolean;
  isLoadingMembers: boolean;
  isLoadingAssignments: boolean;
  error: string | null;
}

const TeamContext = createContext<TeamContextValue | undefined>(undefined);

const now = () => new Date().toISOString();

const initialClientTeamAssignments: ClientTeamAssignment[] = [
  { id: 'cta1', clientId: '1', teamId: 't1', role: 'CONTRIBUTOR', assignedBy: 'u1', assignedAt: now() },
  { id: 'cta2', clientId: '2', teamId: 't2', role: 'CONTRIBUTOR', assignedBy: 'u3', assignedAt: now() },
];

const initialClientMemberAssignments: ClientMemberAssignment[] = [
  { id: 'cma1', clientId: '1', userId: 'u2', role: 'CONTRIBUTOR', assignedBy: 'u1', assignedAt: now() },
];

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamClientAssignments, setTeamClientAssignments] = useState<TeamClientAssignment[]>([]);
  const [clientTeamAssignments, setClientTeamAssignments] = useState<ClientTeamAssignment[]>(initialClientTeamAssignments);
  const [clientMemberAssignments, setClientMemberAssignments] = useState<ClientMemberAssignment[]>(initialClientMemberAssignments);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = () => authService.getAccessToken();

  const refreshTeams = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTeams([]);
      return;
    }
    setIsLoadingTeams(true);
    setError(null);
    try {
      const list = await ApiService.listTeams(token);
      setTeams(list as any as Team[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load teams');
      setTeams([]);
    } finally {
      setIsLoadingTeams(false);
    }
  }, []);

  const loadTeamMembers = useCallback(async (teamId: string) => {
    const token = getToken();
    if (!token || !teamId) return;
    setIsLoadingMembers(true);
    setError(null);
    try {
      const members = await ApiService.listTeamMembers(token, teamId);
      const mapped: TeamMember[] = (members || []).map((m: TeamMemberDto) => ({
        id: m.id,
        teamId,
        email: m.email,
        role: (m.role as ApiTeamRole) as TeamRole,
        status: m.status,
        firstName: m.firstName,
        lastName: m.lastName,
        invitedAt: m.invitedAt,
        joinedAt: m.joinedAt,
      }));
      // Replace members for this teamId
      setTeamMembers(prev => [...prev.filter(x => x.teamId !== teamId), ...mapped]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load team members');
      setTeamMembers(prev => prev.filter(x => x.teamId !== teamId));
    } finally {
      setIsLoadingMembers(false);
    }
  }, []);

  const loadTeamClients = useCallback(async (teamId: string) => {
    const token = getToken();
    if (!token || !teamId) return;
    setIsLoadingAssignments(true);
    setError(null);
    try {
      const assignments = await ApiService.listTeamClients(token, teamId);
      const mapped: TeamClientAssignment[] = (assignments || []).map((a: TeamClientAssignmentDto) => ({
        id: a.id,
        teamId,
        role: a.role,
        createdAt: a.createdAt,
        client: a.client,
      }));
      setTeamClientAssignments(prev => [...prev.filter(x => x.teamId !== teamId), ...mapped]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load team assignments');
      setTeamClientAssignments(prev => prev.filter(x => x.teamId !== teamId));
    } finally {
      setIsLoadingAssignments(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setTeams([]);
      setTeamMembers([]);
      setTeamClientAssignments([]);
      setError(null);
      return;
    }
    void refreshTeams();
  }, [isAuthenticated, refreshTeams]);

  const assignTeamToClient = useCallback(async (clientId: string, teamId: string, role: EngagementRole) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');

    // Backend currently supports only LEAD/CONTRIBUTOR for TeamClient.role.
    // We expose VIEWER in the UI, but persist it as CONTRIBUTOR for now.
    const roleForApi = (role === 'VIEWER') ? 'CONTRIBUTOR' : role;

    setIsLoadingAssignments(true);
    setError(null);
    try {
      await ApiService.assignClientToTeam(token, teamId, { clientId, role: roleForApi as any });

      // Update local client->team view immediately
      setClientTeamAssignments(prev => {
        const existing = prev.find(a => a.clientId === clientId && a.teamId === teamId);
        if (existing) {
          return prev.map(a => (a.clientId === clientId && a.teamId === teamId ? { ...a, role } : a));
        }
        const newAssignment: ClientTeamAssignment = {
          id: `cta_${Date.now()}`,
          clientId,
          teamId,
          role,
          assignedBy: 'self',
          assignedAt: now(),
        };
        return [...prev, newAssignment];
      });

      // Refresh team->client assignment list for the selected team
      await loadTeamClients(teamId);
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [loadTeamClients]);

  const unassignTeamFromClient = useCallback((clientId: string, teamId: string) => {
    setClientTeamAssignments(prev => prev.filter(a => !(a.clientId === clientId && a.teamId === teamId)));
  }, []);

  const addMemberToClient = useCallback((clientId: string, userId: string, role: EngagementRole) => {
    const newAssignment: ClientMemberAssignment = {
      id: `cma_${Date.now()}`,
      clientId,
      userId,
      role,
      assignedBy: 'u1',
      assignedAt: now(),
    };
    setClientMemberAssignments(prev => {
      const exists = prev.find(a => a.clientId === clientId && a.userId === userId);
      if (exists) return prev;
      return [...prev, newAssignment];
    });
  }, []);

  const removeMemberFromClient = useCallback((clientId: string, userId: string) => {
    setClientMemberAssignments(prev => prev.filter(a => !(a.clientId === clientId && a.userId === userId)));
  }, []);

  const createTeam = useCallback(async (input: Pick<Team, 'name' | 'description'>): Promise<Team> => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    setError(null);
    setIsLoadingTeams(true);
    try {
      const created = await ApiService.createTeam(token, { name: input.name, description: input.description || undefined });
      await refreshTeams();
      return created as Team;
    } finally {
      setIsLoadingTeams(false);
    }
  }, [refreshTeams]);

  const deleteTeam = useCallback(async (teamId: string) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    const team = teams.find(t => t.id === teamId);
    const teamName = team?.name?.trim();
    if (!teamId) return;
    if (team?.isProtected || teamName?.toLowerCase() === 'general') {
      throw new Error('The General team is protected and cannot be deleted.');
    }
    setError(null);
    setIsLoadingTeams(true);
    try {
      await ApiService.deleteTeam(token, teamId);
      // Optimistic local cleanup
      setTeams(prev => prev.filter(t => t.id !== teamId));
      setTeamMembers(prev => prev.filter(m => m.teamId !== teamId));
      setTeamClientAssignments(prev => prev.filter(a => a.teamId !== teamId));
      setClientTeamAssignments(prev => prev.filter(a => a.teamId !== teamId));
      await refreshTeams();
    } finally {
      setIsLoadingTeams(false);
    }
  }, [refreshTeams, teams]);

  const addMemberToTeam = useCallback(async (teamId: string, payload: AddTeamMemberPayload) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    if (!teamId) throw new Error('Team required');
    setError(null);
    setIsLoadingMembers(true);
    try {
      await ApiService.addTeamMember(token, teamId, payload);
      await loadTeamMembers(teamId);
      await refreshTeams(); // keep counts fresh
    } finally {
      setIsLoadingMembers(false);
    }
  }, [loadTeamMembers, refreshTeams]);

  const updateTeamMember = useCallback(async (teamId: string, memberId: string, payload: UpdateTeamMemberPayload) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    if (!teamId || !memberId) return;

    const teamName = teams.find(t => t.id === teamId)?.name?.trim().toLowerCase();
    const isGeneralTeam = teamName === 'general';
    const member = teamMembers.find(m => m.teamId === teamId && m.id === memberId);
    if (isGeneralTeam && member?.role === 'OWNER' && payload.role && payload.role !== 'OWNER') {
      throw new Error('The owner of the General team cannot have their role changed.');
    }

    setError(null);
    setIsLoadingMembers(true);
    try {
      const updated = await ApiService.updateTeamMember(token, teamId, memberId, payload);

      // Update names across all loaded teams for this stable memberId
      setTeamMembers(prev => prev.map(m => {
        if (m.id !== memberId) return m;
        const namePatch = {
          ...(payload.firstName !== undefined ? { firstName: payload.firstName } : {}),
          ...(payload.lastName !== undefined ? { lastName: payload.lastName } : {}),
        } as any;

        // Update role only for this specific team
        if (m.teamId === teamId && payload.role) {
          return { ...m, ...namePatch, role: payload.role };
        }
        return { ...m, ...namePatch };
      }));

      // Ensure member list for the team is accurate (covers server-side normalization)
      await loadTeamMembers(teamId);
      await refreshTeams();
      return updated as any;
    } finally {
      setIsLoadingMembers(false);
    }
  }, [loadTeamMembers, refreshTeams, teamMembers, teams]);

  const removeMemberFromTeam = useCallback(async (teamId: string, memberId: string) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    if (!teamId || !memberId) return;

    const teamName = teams.find(t => t.id === teamId)?.name?.trim().toLowerCase();
    const isGeneralTeam = teamName === 'general';
    const member = teamMembers.find(m => m.teamId === teamId && m.id === memberId);
    if (isGeneralTeam && member?.role === 'OWNER') {
      throw new Error('The owner of the General team cannot be removed.');
    }

    setError(null);
    setIsLoadingMembers(true);
    try {
      await ApiService.removeTeamMember(token, teamId, memberId);
      setTeamMembers(prev => prev.filter(m => !(m.teamId === teamId && m.id === memberId)));
      await refreshTeams();
    } finally {
      setIsLoadingMembers(false);
    }
  }, [refreshTeams, teamMembers, teams]);

  const value = useMemo<TeamContextValue>(() => ({
    teams,
    teamMembers,
    clientTeamAssignments,
    teamClientAssignments,
    clientMemberAssignments,
    assignTeamToClient,
    unassignTeamFromClient,
    addMemberToTeam,
    updateTeamMember,
    removeMemberFromTeam,
    loadTeamMembers,
    loadTeamClients,
    addMemberToClient,
    removeMemberFromClient,
    createTeam,
    deleteTeam,
    refreshTeams,
    isLoadingTeams,
    isLoadingMembers,
    isLoadingAssignments,
    error,
  }), [
    teams,
    teamMembers,
    clientTeamAssignments,
    teamClientAssignments,
    clientMemberAssignments,
    assignTeamToClient,
    unassignTeamFromClient,
    addMemberToTeam,
    updateTeamMember,
    removeMemberFromTeam,
    loadTeamMembers,
    loadTeamClients,
    addMemberToClient,
    removeMemberFromClient,
    createTeam,
    deleteTeam,
    refreshTeams,
    isLoadingTeams,
    isLoadingMembers,
    isLoadingAssignments,
    error,
  ]);

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeams = (): TeamContextValue => {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeams must be used within TeamProvider');
  return ctx;
};


